import { computed, effectScope, type EffectScope } from 'vue'
import {
  type RouteLocation,
  useRouter,
} from 'vue-router'
import { useStorage } from '@vueuse/core'

import { axiosInstance } from '@services/api/apiClient'
import { isJWTExpired } from '@libs/fsc/utils/jwtValidator'
import { type LocalStorageAuth } from '@services/Auth/declarations'
import { useGetLegacyPeopleGroupByClub } from '@dashboard/modules/ProfileSelector/composables'
import { useApiUserStoreRequest } from '@services/User/request'
import { isRefreshTokenForbidden } from '@services/api/useRefreshToken/forbiddenScenarios'
import { useUserAuth } from '@core/modules/Auth/composables/useUserAuth'

const storage = useStorage<LocalStorageAuth>('cluber', {
  auth: {
    token: '',
    userIdentifier: '',
    refreshTokenValidity: undefined,
    tokenTtl: undefined,
  },
  currentProfile: undefined,
}, localStorage, { mergeDefaults: true })

// Create a scope for auth composables that can be cleaned up
let authScope: EffectScope | null = null

function handleLegacyAuth(to: RouteLocation, from: RouteLocation, token: string) {
  // LEGACY
  if (to.query?.identifier) {
    const { fetchGetUserProfile } = useApiUserStoreRequest()
    const { getUserProfileDataFromLegacy } = useGetLegacyPeopleGroupByClub()
    storage.value.currentProfile = undefined
    if (storage.value.auth.token) {
      fetchGetUserProfile()
      getUserProfileDataFromLegacy('force')
    }

    const params = new URLSearchParams(window.location.search)
    const identifier = params.get('identifier')

    storage.value.auth.token = identifier as string // THIS LINE IS ONLY REQUIRED FOR LEGACY PURPOSES REMOVE WHEN FULL MIGRATION
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`
  }
  // LEGACY
}

export async function handleRedirects(to: RouteLocation, from: RouteLocation, token: string) {
  // KEEP THE FOLLOWING LINE UNTIL LEGACY MIGRATION (FORCES REDIRECT TO LEGACY)
  if (!useRouter().getRoutes().find(route => route.path.includes(to.fullPath))) {
    if (to.name === 'not-found' && from.fullPath === '/') {
      // This happens when user navigates between legacy and next
      // route doesn't exist in next and cloudfront navigates first to '/'
      try {
        const isRefreshForbidden = isRefreshTokenForbidden({ routeTo: to, routeFrom: from })

        // Clean up previous scope if it exists
        if (authScope) {
          authScope.stop()
          authScope = null
        }

        // Create new scope and run the composable inside it
        authScope = effectScope()
        const result = authScope.run(() => useUserAuth())

        // Make sure result is not undefined
        if (!result) {
          throw new Error('Failed to initialize auth composable')
        }

        await result.handleRefreshTokenRequestProcess(isRefreshForbidden)
      } catch (error) {
        // Clean up scope on error too
        if (authScope) {
          authScope.stop()
          authScope = null
        }

        if (to.meta.isPublic) return true

        return {
          name: 'authLogout',
          query: {
            from: window.location.pathname,
            expiredToken: 'true',
          },
        }
      }
    }

    return true
  }

  if (to.meta.isPublic) return true
  // Note that some redirects on login should be triggered from Login.vue component

  // If no token is set and is accessing the base path (profile selector), redirect to login
  // wihtout warning about token expiration
  if (!token && to.path === '/dashboard/profile-selector') {
    const forbiddenRoutes = window.location.pathname.includes('profile-selector') || window.location.pathname === '/'
    return {
      name: 'authLogin',
      query: {
        ...(!forbiddenRoutes && {
          from: window.location.pathname,
        }),
      },
    }
  }

  // If token is not set or not present on localStorage and the route where you trying to navigate is not login directly
  // the route is saved as query and after succesfull login you will be redirected
  const invalidToken = !token ? true : isJWTExpired(token)

  if (invalidToken && (to.name !== 'authLogin') && from.name !== 'authLogin') {
    if (from.name === 'passwordRecovery') return { name: 'authLogin' } // Redirect to login, after password recovery
    if (to.name === 'authLogout') return { name: 'authLogin' } // Redirect to login, after logout
    return {
      name: 'authLogin',
      query: {
        from: window.location.pathname,
        expiredToken: 'true',
      },
    }
  }

  // If you are already logged into cluber and try to navigate to login page this autoredirects you to the determined paths
  // if the user is trying to logout this redirect is avoided
  if (!isJWTExpired(token) && (to.name === 'authLogin') && (from.name !== 'authLogin')) {
    return { name: 'profileSelector' }
  }

  // Don't break navigation if any of the previous cases are false
  return true
}

export async function userAccessValidation(to: RouteLocation, from: RouteLocation) {
  const currentUserToken = computed(() => storage.value.auth.token)

  handleLegacyAuth(to, from, currentUserToken.value)

  return handleRedirects(to, from, currentUserToken.value)
}
